import React, { Component } from 'react';
import { connect } from 'react-redux';
import { GymResolver } from '../../services';
import moment from 'moment-timezone';
import { translate } from 'react-i18next';
import { ProcessTypes } from 'fitbeat.gym-core';
import Accordion from 'react-native-collapsible/Accordion';
import { Text, View, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity } from 'react-native';
import {
  Icon,
  Avatar,
  Button,
  Heading,
  ButtonTypes,
  HeadingTypes,
} from '../../components';
import Theme from '../../theme';
import { DurationIcon } from '../../ui/components/Icons';
import { FormError } from '../../ui';
import { getMomentFormat, i18n, getSlotTime, Logger, isEmpty } from '../../utils';
import { getNextBookingStep, fetchPartnerProfiles } from '../actions';
import { styles as sharedStyles } from './Styles';
import { getGymTimezone } from '../../utils/gyms';
import { BodyGroupWorkoutSettings } from '../components';
import { studioPartnerInviteAccepted, studioPartnerInviteRejected } from '../../Planner/plannerActions';
import { workoutInvitesSelector } from '../../Planner/plannerSelectors';

class InvitesReceived extends Component {
  static getSubtitle = ({ day, gymTimezone }) => (
    <View style={sharedStyles.subtitleContainer}>
      <Text style={sharedStyles.subtitle}>
        To train on {getMomentFormat(day.date, 'dddd', gymTimezone)}
      </Text>
      <View style={sharedStyles.verticalSeparator} />
      <Text style={sharedStyles.subtitle}>
        {getMomentFormat(day.date, 'DD MMM', gymTimezone)}
      </Text>
    </View>
  );

  constructor(props) {
    super(props);

    this.state = {
      error: null,
      loaded: false,
      activeSection: this.props.invites.length - 1,
    };
  }

  componentDidMount = async () => {
    const { userProfileService: { getUserProfileDetails }, fetchPartnerProfiles } = this.props;
    await fetchPartnerProfiles(this.props.invites, getUserProfileDetails);
    await this.setState({ loaded: true });
  }

  setIsLoading = async (loading) => {
    this.setState({ isLoading: loading });
  }

  setError = (error) => {
    this.setState({ error });
  }

  clearError = () => {
    this.setError(null);
  }

  acceptInvite = async (invite, acceptInviterSettings) => {
    try {
      const { userProfile,
        userProfileService: { refreshCurrentUserProfile },
        workoutSessionService: { acceptInvite }, getNextBookingStep } = this.props;

      this.clearError();
      this.setIsLoading(true);
      const acceptedInvite = await acceptInvite(invite.uniqueReference, acceptInviterSettings);
      this.props.studioPartnerInviteAccepted(acceptedInvite);
      await refreshCurrentUserProfile();
      getNextBookingStep(userProfile, ProcessTypes.BookingMade);
    } catch (error) {
      console.log('error', error);
      this.setError(error);
      Logger.notify(error);
    } finally {
      this.setIsLoading(false);
    }
  }

  rejectInvite = async (invite) => {
    try {
      const { userProfile, invites, workoutSessionService: { rejectInvite }, getNextBookingStep } = this.props;

      this.setIsLoading(true);
      await rejectInvite(invite.uniqueReference);
      this.props.studioPartnerInviteRejected(invite.uniqueReference);
      if (invites.length === 1) {
        getNextBookingStep(userProfile, ProcessTypes.Cancel);
      }
    } catch (error) {
      this.setError(error);
      Logger.notify(error);
    } finally {
      this.setIsLoading(false);
    }
  }

  blockPartner = async (invite, partnerId) => {
    try {
      const { invites, userProfile, workoutSessionService: { blockPartnerAndRejectInvite }, getNextBookingStep } = this.props;
      this.setIsLoading(true);
      await blockPartnerAndRejectInvite(invite, partnerId);
      if (invites.length === 1) {
        getNextBookingStep(userProfile, ProcessTypes.Cancel);
      }
    } catch (error) {
      console.log('error', error);
      this.setError(error);
      Logger.notify(error);
    } finally {
      this.setIsLoading(false);
    }
  }

  renderHeader = (invite, index, isActive) => {
    const { t, partnerProfiles } = this.props;
    const timeLeft = moment(invite.expiresAt).toNow(true);
    const partnerProfile = partnerProfiles[invite.uniqueReference];
    const isFirstInvite = index === 0;
    const gymTimezone = getGymTimezone(invite);

    return (
      <View style={[styles.headerContainer, isFirstInvite && styles.noTopBorder]}>
        <View style={styles.avatarContainer}>
          <Avatar
            iconColor={Theme.GREY_MEDIUM}
            avatarId={invite.pairedSessionUserProfileId}
          />
        </View>
        <View style={styles.headerDetailsContainer}>
          <Heading
            style={styles.fullnameText}
            title={partnerProfile.fullname}
            headerType={HeadingTypes.HEADING_TWO}
          />
          <View style={styles.headerTimeContainer}>
            <DurationIcon
              color={Theme.BLACK}
              width={15}
              height={15}
            />
            <Heading
              style={[styles.timeDetailText, styles.separator]}
              headerType={HeadingTypes.HEADING_THREE}
              title={moment.tz(invite.startTimeOfFirstExercise, gymTimezone).format('HH:mm')}
            />
            <Text style={[styles.timeDetailText, styles.timeSeparator]}>|</Text>
            <Heading
              style={styles.timeDetailText}
              headerType={HeadingTypes.HEADING_THREE}
              title={`${getSlotTime(invite)}`}
            />
          </View>
          <Heading
            style={styles.expiresTime}
            headerType={HeadingTypes.HEADING_THREE}
            title={t('inviteExpiresIn', { timeLeft })}
          />
        </View>
        <View style={styles.arrowContainer}>
          <Icon
            size={25}
            color={Theme.GREY_MEDIUM}
            name={isActive ? 'ios-arrow-up' : 'ios-arrow-down'}
          />
        </View>
      </View>
    );
  }

  renderContent = (invite) => {
    const { t, partnerProfiles, userProfile: { privateProfile = false }, workoutSession } = this.props;
    const { isLoading } = this.state;
    const gym = GymResolver.getByName(workoutSession.gymName);

    if (isLoading) {
      return (
        <View style={styles.workoutSettingsContainer}>
          <ActivityIndicator
            size="small"
            color={Theme.PRIMARY}
            animating={isLoading}
            style={styles.activityIndicator}
          />
        </View>
      )
    }

    if (!invite || !invite.pairedSession) {
      return (
        <View style={styles.workoutSettingsContainer}>
          <FormError error={{message: t('inviteCancelled')}} />
        </View>
      );
    }

    const { pairedSession: { settings } } = invite;
    const { uid, fullname, gender } = partnerProfiles[invite.uniqueReference];

    return (
      <View style={styles.workoutSettingsContainer}>
        <Heading
          style={styles.fullnameContentHeader}
          headerType={HeadingTypes.HEADING_TWO}
          title={t('partnersNameWorkoutSettings', { fullname })}
        />
        <BodyGroupWorkoutSettings
          gender={gender}
          workoutSettings={settings}
          duration={getSlotTime(invite.pairedSession)}
          gym={gym}
        />
          <View>
            <Button
              fontSize={12}
              buttonType={ButtonTypes.OUTLINE}
              containerStyle={styles.buttonContainer}
              title={t('acceptParterSettings')}
              onPress={() => this.acceptInvite(invite, true)}
            />
            <Button
              fontSize={12}
              buttonType={ButtonTypes.OUTLINE}
              containerStyle={styles.buttonContainer}
              title={t('acceptInviteMySettings')}
              onPress={() => this.acceptInvite(invite, false)}
            />
            <Button
              fontSize={12}
              title={t('unavailable')}
              buttonType={ButtonTypes.OUTLINE}
              onPress={() => this.rejectInvite(invite)}
              containerStyle={styles.buttonContainer}
            />
            {!privateProfile &&
              <Button
                fontSize={12}
                title={t('blockInvite')}
                buttonType={ButtonTypes.OUTLINE}
                onPress={() => this.blockPartner(invite, uid)}
                containerStyle={styles.buttonContainer}
              />
            }
          </View>
      </View>
    );
  }

  setActiveSection = async (index) => {
    await this.setState({ activeSection: index });
  }

  render() {
    const { invites, isLoading } = this.props;
    const { error, loaded, activeSection } = this.state;

    if (isLoading || !loaded) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator
            size='small'
            color={Theme.PRIMARY}
            animating={true}
          />
        </View>
      );
    }

    return (
      <ScrollView style={[sharedStyles.containerNoPadding, { marginHorizontal: -15 }]}>
        <Accordion
          sections={invites}
          renderHeader={this.renderHeader}
          renderContent={this.renderContent}
          touchableComponent={TouchableOpacity}
          activeSection={activeSection}
          onChange={this.setActiveSection}
        />
        <View style={styles.formErrorContainer}>
          <FormError error={error} />
        </View>
      </ScrollView>
    );
  }
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  headerContainer: {
    borderTopWidth: 1,
    marginHorizontal: 15,
    paddingVertical: 10,
    flexDirection: 'row',
    backgroundColor: Theme.WHITE,
    borderTopColor: Theme.GREY_LIGHT,
  },
  noTopBorder: {
    borderTopWidth: 0,
  },
  trainingDayText: {
    fontSize: 16,
    marginTop: -10,
    marginBottom: 10,
    paddingHorizontal: 15,
    color: Theme.CHARCOAL,
  },
  avatarContainer: {
    paddingRight: 5,
  },
  headerDetailsContainer: {
    paddingHorizontal: 5,
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  fullnameText: {
    fontSize: 16,
    fontFamily:Theme.CustomBold,
    color: Theme.CHARCOAL,
  },
  fullnameContentHeader: {
    fontSize: 16,
    fontFamily:Theme.CustomBold,
    color: Theme.CHARCOAL,
  },
  headerTimeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  separator: {
    paddingLeft: 5,
  },
  timeDetailText: {
    fontSize: 13,
    fontFamily:Theme.Custom500,
    paddingVertical: 3,
    color: Theme.CHARCOAL,
  },
  timeSeparator: {
    color: Theme.GREY,
    paddingHorizontal: 5,
  },
  expiresTime: {
    fontSize: 13,
    color: Theme.CHARCOAL,
  },
  arrowContainer: {
    flex: 1,
    alignItems: 'flex-end',
  },
  workoutSettingsContainer: {
    padding: 15,
    backgroundColor: '#fafafa',
  },
  buttonContainer: {
    marginVertical: 3,
  },
  formErrorContainer: {
    paddingHorizontal: 10,
  },
});

const mapStateToProps = (state) => {
  return {
    invites: workoutInvitesSelector(state),
    workoutSession: state.bookings.bookingWizard.workoutSession,
    partnerProfiles: state.bookings.bookingWizard.partnerProfiles,
    isLoading: state.bookings.bookingWizard.isFetchingPartnerProfiles,
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    fetchPartnerProfiles: (invites, getUserProfileDetails) => dispatch(fetchPartnerProfiles(invites, getUserProfileDetails)),
    getNextBookingStep: (userProfile, processType) => dispatch(getNextBookingStep(userProfile, processType)),
    studioPartnerInviteAccepted: (acceptedInvite) => dispatch(studioPartnerInviteAccepted(acceptedInvite)),
    studioPartnerInviteRejected: (rejectedWorkoutId) => dispatch(studioPartnerInviteRejected(rejectedWorkoutId)),
  };
};

InvitesReceived = connect(mapStateToProps, mapDispatchToProps)(InvitesReceived);
InvitesReceived = translate(['bookings', 'userSettings', 'bookings'], { wait: true })(InvitesReceived);
export default InvitesReceived;
